import { useCallback } from 'react';

import { useActorRef, useSelector } from '@xstate5/react';

import { useTelemetry } from '@order/Telemetry';

import { useClaimReceiptActor, useFetchReceiptActor } from './actors';
import { receiptMachine } from './receipt.machine';

import type { ReceiptMachineImplementations } from './receipt.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useReceiptMachine = (receiptId: string) => {
  const { track } = useTelemetry();

  // ─── Actors ──────────────────────────────────────────────────────────

  const { fetchReceipt } = useFetchReceiptActor();
  const { claimReceipt } = useClaimReceiptActor();

  // ─── Actions ─────────────────────────────────────────────────────────

  const onSuccessfulClaim = useCallback<NonNullable<Actions['onSuccessfulClaim']>>(() => {
    track('receipt-claim.claim-successful');
  }, [track]);

  const onExpired = useCallback<NonNullable<Actions['onExpired']>>(() => {
    track('receipt-claim.claim-error', { userError: 'expired' });
  }, [track]);

  const onTimeRestriction = useCallback<NonNullable<Actions['onTimeRestriction']>>(() => {
    track('receipt-claim.claim-error', { userError: 'time-restriction' });
  }, [track]);

  const onAlreadyClaimed = useCallback<NonNullable<Actions['onAlreadyClaimed']>>(() => {
    track('receipt-claim.claim-error', { userError: 'already-claimed' });
  }, [track]);

  const onNotFound = useCallback<NonNullable<Actions['onNotFound']>>(() => {
    track('receipt-claim.claim-error', { userError: 'not-found' });
  }, [track]);

  const onUnknownError = useCallback<NonNullable<Actions['onUnknownError']>>(() => {
    track('receipt-claim.claim-error', { userError: 'unknown-error' });
  }, [track]);

  // ─── Machine ─────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    receiptMachine.provide({
      actors: {
        fetchReceipt,
        claimReceipt,
      },
      actions: {
        onSuccessfulClaim,
        onExpired,
        onTimeRestriction,
        onAlreadyClaimed,
        onNotFound,
        onUnknownError,
      },
    }),
    { input: { receiptId } },
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isFetchingReceipt = useSelector(actorRef, (snapshot) => snapshot.matches('fetching-receipt')); // prettier-ignore
  const isClaiming = useSelector(actorRef, (snapshot) => snapshot.matches('claiming'));
  const isClaimed = useSelector(actorRef, (snapshot) => snapshot.matches('claimed'));

  const isExpiredError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'expired' })); // prettier-ignore
  const isTimeRestrictionError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'time-restricted' })); // prettier-ignore
  const isReceiptAlreadyClaimedError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'already-claimed' })); // prettier-ignore
  const isTransactionNotFoundError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'not-found' })); // prettier-ignore
  const isUnknownError = useSelector(actorRef, (snapshot) => snapshot.matches({ error: 'unknown-error' })); // prettier-ignore

  const hasError = useSelector(actorRef, (snapshot) => snapshot.matches('error'));

  // ─── Context ─────────────────────────────────────────────────────────

  const error = useSelector(actorRef, (snapshot) => snapshot.context.error);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetchingReceipt,
    isClaiming,
    isClaimed,
    isExpiredError,
    isTimeRestrictionError,
    isReceiptAlreadyClaimedError,
    isTransactionNotFoundError,
    isUnknownError,
    hasError,
    error,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Actions = NonNullable<ReceiptMachineImplementations['actions']>;
